import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Save, X, FileText, Trash } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { surahAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const SurahManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSurah, setEditingSurah] = useState(null);
  const [selectedSurahs, setSelectedSurahs] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    nameArabic: '',
    verses: 0,
    revelationType: 'Mekke',
    order: 1
  });

  useEffect(() => {
    loadSurahs();
  }, []);

  const loadSurahs = async () => {
    try {
      const response = await surahAPI.getAll();
      setSurahs(response.data);
      setSelectedSurahs([]);
    } catch (error) {
      console.error('Error loading surahs:', error);
      toast({
        title: 'Hata!',
        description: 'Sureler yuklenirken hata olustu.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSurah = (surahId) => {
    setSelectedSurahs(prev => 
      prev.includes(surahId)
        ? prev.filter(id => id !== surahId)
        : [...prev, surahId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSurahs.length === surahs.length) {
      setSelectedSurahs([]);
    } else {
      setSelectedSurahs(surahs.map(s => s.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await surahAPI.bulkDelete(selectedSurahs);
      toast({
        title: 'Basarili!',
        description: `${selectedSurahs.length} sure basariyla silindi.`
      });
      setIsDeleteModalOpen(false);
      loadSurahs();
    } catch (error) {
      console.error('Error bulk deleting surahs:', error);
      toast({
        title: 'Hata!',
        description: 'Sureler silinirken hata olustu.',
        variant: 'destructive'
      });
    }
  };

  const handleOpenDialog = (surah = null) => {
    if (surah) {
      setEditingSurah(surah);
      setFormData({
        name: surah.name,
        nameArabic: surah.nameArabic,
        verses: surah.verses,
        revelationType: surah.revelationType,
        order: surah.order
      });
    } else {
      setEditingSurah(null);
      setFormData({
        name: '',
        nameArabic: '',
        verses: 0,
        revelationType: 'Mekke',
        order: surahs.length + 1
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingSurah(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['verses', 'order'].includes(name) ? parseInt(value) : value
    }));
  };

  const generateSlug = (text) => {
    // Generate slug from Turkish text
    const slug = text
      .toLowerCase()
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    
    console.log('[SurahManagement] Generated slug:', text, '→', slug);
    return slug;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Generate slug from name
      const dataToSend = {
        ...formData,
        slug: generateSlug(formData.name)
      };
      
      console.log('[SurahManagement] Submitting surah:', dataToSend);
      
      if (editingSurah) {
        await surahAPI.update(editingSurah.id, dataToSend);
        toast({
          title: 'Basarili!',
          description: 'Sure basariyla guncellendi.'
        });
      } else {
        await surahAPI.create(dataToSend);
        toast({
          title: 'Basarili!',
          description: 'Sure basariyla eklendi.'
        });
      }
      handleCloseDialog();
      loadSurahs();
    } catch (error) {
      console.error('[SurahManagement] Error saving surah:', error);
      console.error('[SurahManagement] Error details:', error.response?.data);
      
      // Handle different error response formats
      let errorMessage = 'Sure kaydedilirken hata olustu.';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle FastAPI validation errors (array of error objects)
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Hata!',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu sureyi ve tum ayetlerini silmek istediginizden emin misiniz?')) return;
    
    try {
      await surahAPI.delete(id);
      toast({
        title: 'Basarili!',
        description: 'Sure basariyla silindi.'
      });
      loadSurahs();
    } catch (error) {
      console.error('Error deleting surah:', error);
      toast({
        title: 'Hata!',
        description: 'Sure silinirken hata olustu.',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#8B7355]">Yukleniyor...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-[#5C4033]">Sure Yonetimi</h2>
        <div className="flex space-x-2">
          {selectedSurahs.length > 0 && (
            <Button
              onClick={() => setIsDeleteModalOpen(true)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash className="h-4 w-4 mr-2" />
              Seçilenleri Sil ({selectedSurahs.length})
            </Button>
          )}
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Yeni Sure Ekle
          </Button>
        </div>
      </div>

      {/* Select All */}
      {surahs.length > 0 && (
        <div className="mb-4">
          <label className="flex items-center space-x-2 text-sm text-[#6B5744] cursor-pointer">
            <input
              type="checkbox"
              checked={selectedSurahs.length === surahs.length}
              onChange={handleSelectAll}
              className="w-4 h-4 text-[#8B7355] border-[#D5C4A1] rounded focus:ring-[#8B7355]"
            />
            <span>Tümünü Seç / Temizle</span>
          </label>
        </div>
      )}

      <div className="space-y-3">
        {surahs.map((surah) => (
          <Card key={surah.id} className="bg-white border-[#E8DCC8] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedSurahs.includes(surah.id)}
                  onChange={() => handleSelectSurah(surah.id)}
                  className="w-5 h-5 text-[#8B7355] border-[#D5C4A1] rounded focus:ring-[#8B7355]"
                />

                <div className="flex-shrink-0 w-10 h-10 bg-[#D5C4A1] rounded-full flex items-center justify-center text-[#5C4033] font-bold">
                  {surah.order}
                </div>

                <div className="cursor-pointer flex-1" onClick={() => navigate(`/admin/surahs/${surah.id}/verses`)}>
                  <h3 className="font-bold text-[#3E3028] hover:text-[#8B7355] transition-colors">
                    {surah.name}
                  </h3>
                  <p className="text-sm text-[#8B7355]">
                    {surah.nameArabic} • {surah.verses} Ayet • {surah.revelationType}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/admin/surahs/${surah.id}/verses`)}
                  className="text-[#8B7355] hover:bg-[#F5EFE7]"
                >
                  <FileText className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDialog(surah)}
                  className="text-[#8B7355] hover:bg-[#F5EFE7]"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(surah.id)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {surahs.length === 0 && (
        <Card className="bg-white border-[#E8DCC8] p-12">
          <div className="text-center">
            <p className="text-[#8B7355]">
              Henuz sure eklenmemis. Yeni sure eklemek icin yukardaki butona tiklayin.
            </p>
          </div>
        </Card>
      )}

      {/* Edit/Add Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#F9F6F1]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#5C4033]">
              {editingSurah ? 'Sure Duzenle' : 'Yeni Sure Ekle'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Sure Adi</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Fatiha"
                  className="bg-white border-[#D5C4A1]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Arapca Adi</label>
                <Input
                  name="nameArabic"
                  value={formData.nameArabic}
                  onChange={handleChange}
                  required
                  placeholder="الفاتحة"
                  className="bg-white border-[#D5C4A1] text-right"
                  style={{ fontFamily: '"Traditional Arabic", serif' }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Ayet Sayisi</label>
                <Input
                  name="verses"
                  type="number"
                  value={formData.verses}
                  onChange={handleChange}
                  required
                  min="1"
                  className="bg-white border-[#D5C4A1]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Inis Turu</label>
                <select
                  name="revelationType"
                  value={formData.revelationType}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-[#D5C4A1] rounded-md bg-white text-[#5C4033]"
                >
                  <option value="Mekke">Mekke</option>
                  <option value="Medine">Medine</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Sira Numarasi</label>
              <Input
                name="order"
                type="number"
                value={formData.order}
                onChange={handleChange}
                required
                min="1"
                className="bg-white border-[#D5C4A1]"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCloseDialog}
                className="text-[#6B5744]"
              >
                <X className="h-4 w-4 mr-2" />
                Iptal
              </Button>
              <Button
                type="submit"
                className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {editingSurah ? 'Guncelle' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">⚠️ Toplu Silme Onayı</DialogTitle>
            <DialogDescription>
              <p className="text-base mt-2">
                <strong>{selectedSurahs.length} adet</strong> sureyi ve tüm ayetlerini silmek üzeresiniz.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Bu işlem geri alınamaz! Devam etmek istediğinizden emin misiniz?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex space-x-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              İptal
            </Button>
            <Button
              type="button"
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash className="h-4 w-4 mr-2" />
              Evet, Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SurahManagement;
